import {
    Component,
    defineElement,
    FC,
    onDidLoad,
    useElement,
    useListen,
    useProp,
} from '@atomify/hooks';
import { h } from '@atomify/jsx';
import { ModalWrapper } from '@atoms/modal-wrapper/modal-wrapper';
import {
    closeModal,
    isModalActive,
    modalExists,
    modalStore,
    openModal,
} from '@source/store/modals';
import { GTMPush } from '@utilities/gtm';
import { useStylesheet } from '@utilities/hooks';

import style from './iframe-modal.scss';

interface IframeModalData {
    close?: boolean;
}

export interface IframeModal extends Component {
    id: string;
    src: string;
    modalTitle: string;
    minWidth: string;
    minHeight: string;
    classes: string;
    active: boolean;
}

const BpdIframeModal: FC<IframeModal> = ({ element }) => {
    useStylesheet(style);
    const modalWrapperContent = useElement<HTMLDivElement>('[js-hook-modal-wrapper-content]');

    const [id] = useProp<string>('id');
    const [iframeId] = useProp<string | null>('iframeId', null);
    const [, setActive, watchActive] = useProp<boolean>('active', false);
    const [src] = useProp<string>('src', '');
    const [modalTitle] = useProp<string>('modalTitle', '');
    const [minWidth] = useProp<string>('minWidth', '100%');
    const [minHeight] = useProp<string>('minHeight', '50vh');
    const [useCssDimensions] = useProp<boolean>('useCssDimensions', false);
    const [noPadding] = useProp<boolean>('noPadding', false);
    const [classes] = useProp<string>('classes', '');
    const [trackState] = useProp<boolean>('trackState', false);

    let iframeCreated: boolean = false;

    const GTM_DEFAULT_PROPS: DataLayerElement = {
        event: 'iframe-modal-state',
        modalId: element.id,
        ...element.dataset,
    };

    const wrapperClasses = {
        'bpd-iframe-modal__container': true,
        [classes]: !!classes,
    };

    onDidLoad(() => {
        if (element.active) open(true);

        modalStore.subscribe(
            ({ modals }) => {
                const { active } = modals[id];
                setActive(active);
            },
            ['modals'],
        );
    });

    watchActive(open);

    function open(active: boolean) {
        if (!active) {
            trackState && GTMPush({ ...GTM_DEFAULT_PROPS, eventAction: 'close' });
            return;
        }

        if (!iframeCreated) {
            const iframe = createIframe();
            modalWrapperContent.current?.appendChild(iframe);
            iframeCreated = true;
        } else {
            if (modalExists(id) && !isModalActive(id)) openModal(id);
        }

        trackState && GTMPush({ ...GTM_DEFAULT_PROPS, eventAction: 'open' });
    }

    useListen(window, 'message', iframeMessageReceived);
    function iframeMessageReceived(e: MessageEvent) {
        const { data, modalId } = e.data;

        if (modalId === id) {
            runAction(data);
        }
    }

    function runAction(data: IframeModalData) {
        const closeAction = data.close;
        if (closeAction) closeModal(id);
    }

    function createIframe() {
        const iframe = document.createElement('iframe');

        iframe.src = src;
        if (!useCssDimensions) {
            iframe.setAttribute('style', `min-width: ${minWidth}; min-height: ${minHeight};`);
        }

        if (iframeId) iframe.id = iframeId;

        iframe.onload = () => {
            if (!isModalActive(id)) openModal(id);
        };

        return iframe;
    }

    return (
        <div class={wrapperClasses}>
            <ModalWrapper id={id} active={false} title={modalTitle} noPadding={noPadding} />
        </div>
    );
};

BpdIframeModal.props = {
    id: { type: String, required: true, reflectToAttr: true },
    iframeId: { type: String, reflectToAttr: true },
    active: { type: String, reflectToAttr: true },
    src: { type: String, reflectToAttr: true },
    modalTitle: { type: String },
    minWidth: { type: String },
    minHeight: { type: String },
    useCssDimensions: { type: Boolean },
    noPadding: { type: Boolean },
    classes: { type: String },
    trackState: { type: Boolean },
};

defineElement('bpd-iframe-modal', BpdIframeModal);
